<template>
  <v-row align="stretch">
    <v-col cols="12">
      <HeroBanner
        :title="t('frontpage_goodday')"
        :text="authUser?.name ?? ''"
        background-image-url="/assets/banner-image.jpg"
      />
    </v-col>

    <!-- Signing Notification Banner -->
    <v-row v-if="undirritunNotify" align="stretch" style="padding: 2rem;">
      <RouterLink
        :to="{
          name: 'Undirritun',
          query: {
            docId: undirritunNotifyModel[0]?.docId,
            projectId: undirritunNotifyModel[0]?.projectId,
            type: String(resolveUmsoknType(undirritunNotifyModel[0]?.type) ?? ''),
            ssn: undirritunNotifyModel[0]?.ssn,
            cosigner: 'true'
          }
        }"
        class="signing-banner no-decoration"
      >
        <v-col cols="12">
          <p>
            <strong>Skjal til undirritunar:</strong>
            Þín bíður skjal til undirritunar, vinsamlegast smelltu hér til að skoða.
          </p>
        </v-col>
      </RouterLink>
    </v-row>

    <v-col cols="12" md="8">
      <IdgjaldaTable :idgjold="idgjold" class="fill-height" />
    </v-col>

    <v-col cols="12" md="4">
      <LastPayment
        :idgjald="latestPayment?.idgjald ?? 0"
        :motframlag="latestPayment?.motframlag ?? 0"
        :loading="idgjold === null"
        class="fill-height"
      />
    </v-col>
  </v-row>

  <v-row align="stretch">
        <v-col md="12" sm="12">
      <LoanRights is-frontpage />
    </v-col>
    <v-col cols="12" md="4">
      <RouterLink to="/lifeyrir/rettindi" class="no-decoration">
        <FrontPageBox
          :title="t('lifeyrisrettindi_gildi')"
          :amount="rettindi ?? 0"
          :subtitle="t('greidslur_manudi')"
        />
      </RouterLink>
    </v-col>

    <v-col cols="12" md="4">
      <RouterLink to="/sereign" class="no-decoration">
        <FrontPageBox
          :title="t('sereign_gildi')"
          :amount="sereign ?? 0"
        />
      </RouterLink>
    </v-col>

    <v-col cols="12" md="4">
      <FrontPageBox
        :title="t('tilgreind_sereign_gildi')"
        :amount="tilgreindSereign ?? 0"
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="12" md="4">
      <BaseCard>
        <RecentDocuments />
      </BaseCard>
    </v-col>
    <!-- <v-col md="4" sm="12">
      <LoanRights is-frontpage />
    </v-col> -->
  </v-row>
</template>

<script lang="ts" setup>
import { onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useJoakimStore } from '@/stores/useJoakimStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMessages } from '@/composables/useMessages'
import { resolveUmsoknType } from '@/types/UmsoknType'

import IdgjaldaTable from '@/components/idgjald/IdgjaldaTable.vue'
import HeroBanner from '@/components/shared/HeroBanner.vue'
import LastPayment from '@/components/LastPayment/LastPayment.vue'
import FrontPageBox from '@/components/shared/FrontPageBox.vue'
import LoanRights from '@/components/loanrights/LoanRights.vue'
import BaseCard from '@/components/shared/BaseCard.vue'
import RecentDocuments from '@/components/Joakim/RecentDocuments.vue'

type PaymentRow = {
  dagsGreitt?: string
  idgjaldaManudur?: string
  greitt?: boolean
  idgjald?: number
  samtals?: number
  motframlag?: number
  // allow extra fields
  [k: string]: unknown
}

const { t } = useI18n()

const joakim = useJoakimStore()
const auth = useAuthStore()

const {
  idgjold,
  rettindi,
  sereign,
  tilgreindSereign,
  lifeyrirSum,
} = storeToRefs(joakim)
const { authUser } = storeToRefs(auth)

const {
  undirritunNotify,
 undirritunNotifyModel,
  loadSignatureNotifications,
} = useMessages()

onMounted(async () => {
  try {
    if (idgjold.value == null) await joakim.getIdgjold()
    if (authUser.value == null) await auth.getAuthUser()

    if (sereign.value == null || tilgreindSereign.value == null) {
      await joakim.setSereign()
    }
    if (lifeyrirSum.value == null) await joakim.getSumLifeyrisgattBoxes()
    if (rettindi.value == null) await joakim.setRettindi()

    // ⭐ NEW: Load signing notifications
    await loadSignatureNotifications()

  } catch (e) {
    console.error(e)
  }
})


const latestPayment = computed<null | ({ idgjald: number; motframlag: number } & PaymentRow)>(() => {
  const source = idgjold.value as unknown

  let rows: PaymentRow[] = []
  if (Array.isArray(source)) {
    rows = source as PaymentRow[]
  } else if (source && Array.isArray((source as any).idgjold)) {
    rows = (source as any).idgjold as PaymentRow[]
  }

  // Rows without dagsGreitt are months that haven't been paid yet
  const paid = rows.filter(r => !!r.dagsGreitt && (Number(r.samtals) || 0) >= 0)
  if (paid.length === 0) return null

  const timestamp = (r: PaymentRow) => new Date(r.dagsGreitt as string).getTime() || 0

  const r = [...paid].sort((a, b) => timestamp(b) - timestamp(a))[0]
  if (!r) return null

  return {
    ...r,
    idgjald: Number(r.idgjald) || 0,
    motframlag: Number(r.motframlag) || 0,
  }
})
</script>

<style scoped lang="scss">
.iframe-wrapper {
  width: 100%;
  overflow: hidden;

  iframe {
    width: 100%;
    min-height: 2400px;
    border: none;
    display: block;
  }
}

.no-decoration {
  display: block;
  color: inherit;
  text-decoration: none;
  height: 100%;

  &:hover { text-decoration: none; }
}

.signing-banner {
  background: #194458;
  padding: 16px;
  border-radius: 12px;
  display: block;
  min-width: 100%;

  p {
    margin: 0;
    color: #fff;
    font-weight: 500;
  }
}
</style>
